import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  FaCamera,
  FaCalendarAlt,
  FaCheckCircle,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPen,
  FaPhone,
  FaBullhorn,
  FaFileAlt,
} from 'react-icons/fa';
import './Profile.css';
import '../dashboard/profile/ProfileForm.css';
import '../dashboard/feed/FeedSection.css';
import {
  fetchUserPublicProfile,
  registerUserProfileVisit,
  updateUserHero,
  updateUserAvatar,
  updateUserAboutMe,
  updateUserProfileBasics,
  updateUserPassword,
} from '../../services/profileApi';
import { getCurrentAuthUser, onAuthStateChanged } from '../../firebaseAuth';
import { DEFAULT_USER_AVATAR } from '../../constants/media';
import { searchUnlocodes } from '../../services/unlocodeService';
import { listFeedPosts } from '../../services/feedPostsApi';
import { listServiceReviews } from '../../services/reviewsApi';
import ReviewsShowcase from '../../components/ReviewsShowcase';

const buildInitials = text => {
  if (!text) return 'U';
  return (
    text
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase() || '')
      .join('') || 'U'
  );
};

const USER_VISIT_STORAGE_KEY = 'flotte:user-profile:last-visit';
const USER_VISIT_COOLDOWN_MS = 10 * 60 * 1000;
const RECENT_ACTIVITY_LIMIT = 5;

const formatDateLabel = iso => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const formatFollowers = value => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  const total = Math.max(0, Math.round(value));
  return `${new Intl.NumberFormat('es-MX').format(total)} seguidores`;
};

const formatVisitCount = value => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return '';
  }
  const rounded = Math.max(0, Math.round(numeric));
  return `${new Intl.NumberFormat('es-MX').format(rounded)} visitas`;
};

const splitParagraphs = text => {
  if (!text) return [];
  return text
    .split(/\r?\n/)
    .map(part => part.trim())
    .filter(Boolean);
};

const buildActivityPreview = (text, limit = 220) => {
  const normalized = (text || '').replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return 'Publicación sin contenido.';
  }
  if (normalized.length <= limit) {
    return normalized;
  }
  return `${normalized.slice(0, limit - 3)}...`;
};

const formatRouteLabel = request => {
  const from = (request?.pickupLocation || '').trim();
  const to = (request?.dropoffLocation || '').trim();
  if (from && to) {
    return `${from} → ${to}`;
  }
  return from || to || '';
};

const formatBudgetLabel = value => {
  if (value === null || value === undefined || value === '') return '';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(
    Math.max(0, numeric)
  );
};

const buildActivityDescriptor = post => {
  const dateLabel = formatDateLabel(post.created) || 'Reciente';
  const descriptor = {
    label: 'Publicación',
    type: 'publication',
    icon: 'publication',
    title: buildActivityPreview(post.content, 140),
    metaPill: '',
    dateLabel,
    href: '',
    external: false,
  };

  if (post.attachedRequest) {
    const request = post.attachedRequest;
    descriptor.type = 'request';
    descriptor.icon = 'request';
    descriptor.label = 'Solicitud';
    const routeLabel = formatRouteLabel(request);
    descriptor.title = routeLabel || request.title || descriptor.title;
    const budget = formatBudgetLabel(request.budget);
    descriptor.metaPill = budget ? `Presupuesto ${budget}` : '';
    descriptor.href = request?.id ? `/solicitudes/${request.id}` : '';
  } else if (post.newsLink) {
    descriptor.label = 'Noticia';
    descriptor.title = buildActivityPreview(post.content || post.newsLink, 140);
    descriptor.href = post.newsLink;
    descriptor.external = true;
  } else if (post.isNews) {
    descriptor.label = 'Actualización';
    descriptor.title = buildActivityPreview(post.content, 140);
  }

  return descriptor;
};

export default function UserPublicProfile() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingHero, setUpdatingHero] = useState(false);
  const [updatingAvatar, setUpdatingAvatar] = useState(false);
  const [isEditingAbout, setIsEditingAbout] = useState(false);
  const [aboutDraft, setAboutDraft] = useState('');
  const [savingAbout, setSavingAbout] = useState(false);
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [contactDraft, setContactDraft] = useState({ phoneNumber: '', baseOperacion: '' });
  const [savingContact, setSavingContact] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [passwordDraft, setPasswordDraft] = useState({ newPassword: '', confirmPassword: '' });
  const [savingPassword, setSavingPassword] = useState(false);
  const [recentPosts, setRecentPosts] = useState([]);
  const [recentPostsLoading, setRecentPostsLoading] = useState(false);
  const [recentPostsError, setRecentPostsError] = useState('');
  const [clientReviews, setClientReviews] = useState([]);
  const [clientReviewsLoading, setClientReviewsLoading] = useState(false);
  const heroInputRef = useRef(null);
  const avatarInputRef = useRef(null);
	const [authModel, setAuthModel] = useState(() => getCurrentAuthUser());
	useEffect(() => onAuthStateChanged(setAuthModel), []);
	const isSelf = Boolean(authModel?.id && profile?.id && authModel.id === profile.id);
	const profileIdValue = profile?.id || '';
	const profileUsernameValue = (profile?.username || '').trim();
	const profileCompanyIdValue = profile?.companyId || '';
	const profileAllowVisitorDetails = Boolean(profile?.allowVisitorDetails);
	const profileVisitSummary = useMemo(
		() => ({
			id: profileIdValue,
			companyId: profileCompanyIdValue,
			allowVisitorDetails: profileAllowVisitorDetails,
		}),
		[profileIdValue, profileCompanyIdValue, profileAllowVisitorDetails]
	);

  const resolveProfileIdentifier = useCallback(() => {
    const fromRoute = String(username ?? '').trim();
    if (fromRoute) return fromRoute;
    if (profile?.username) return profile.username;
    if (profile?.id) return profile.id;
    return '';
  }, [username, profile?.username, profile?.id]);

  const refreshProfile = useCallback(async () => {
    const identifier = resolveProfileIdentifier();
    if (!identifier) return;
    try {
      const record = await fetchUserPublicProfile(identifier);
      setProfile(record);
    } catch (err) {
      setError(err?.message || 'No se pudo actualizar el perfil.');
    }
  }, [resolveProfileIdentifier]);

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      const identifier = String(username ?? '').trim();
      if (!identifier) {
        setProfile(null);
        setError('Perfil no encontrado.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const record = await fetchUserPublicProfile(identifier);
        if (!ignore) {
          setProfile(record);
        }
      } catch (err) {
        if (!ignore) {
          setProfile(null);
          setError(err?.message || 'No se pudo cargar el perfil.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, [username]);

  useEffect(() => {
    if (!profile?.id) {
      setClientReviews([]);
      return;
    }
    let ignore = false;
    setClientReviewsLoading(true);
    listServiceReviews({
      targetUserId: profile.id,
      direction: 'provider_to_client',
      limit: 6,
    })
      .then(response => {
        if (!ignore) {
          setClientReviews(response?.items || []);
        }
      })
      .catch(() => {
        if (!ignore) {
          setClientReviews([]);
        }
      })
      .finally(() => {
        if (!ignore) {
          setClientReviewsLoading(false);
        }
      });
    return () => {
      ignore = true;
    };
  }, [profile?.id]);

  useEffect(() => {
    if (!profile) return;
    if (!isEditingAbout) {
      setAboutDraft(profile.aboutMe || '');
    }
    if (!isEditingContact) {
      setContactDraft({
        phoneNumber: profile.phoneNumber || '',
        baseOperacion: profile.baseOperacion || '',
      });
      setLocationSuggestions([]);
    }
  }, [profile, isEditingAbout, isEditingContact]);

  useEffect(() => {
    if (!isSelf || !isEditingContact) {
      setLocationSuggestions([]);
      return;
    }
    const query = contactDraft.baseOperacion.trim();
    if (!query || query.length < 2) {
      setLocationSuggestions([]);
      return;
    }
    let ignore = false;
    setLocationLoading(true);
    searchUnlocodes(query, 5)
      .then(results => {
        if (!ignore) {
          setLocationSuggestions(results || []);
        }
      })
      .catch(() => {
        if (!ignore) {
          setLocationSuggestions([]);
        }
      })
      .finally(() => {
        if (!ignore) {
          setLocationLoading(false);
        }
      });
    return () => {
      ignore = true;
    };
  }, [contactDraft.baseOperacion, isEditingContact, isSelf]);

  const view = useMemo(() => {
    const normalizedName = (profile?.name || profile?.username || profile?.email || 'Miembro de Flotte').trim();
    const usernameValue = (profile?.username || '').trim();
    const companyName = (profile?.companyName || '').trim();
    const companySlug = (profile?.companySlug || '').trim();
    const aboutParagraphs = splitParagraphs(profile?.aboutMe || '');

    const heroUrl = (profile?.heroUrl || '').trim();

    return {
      name: normalizedName,
      username: usernameValue,
      tagline:
        (profile?.tagline || '').trim() ||
        (companyName ? `Miembro de ${companyName}` : 'Miembro de la red Flotte'),
      heroUrl,
      avatarUrl: profile?.avatarUrl || '',
      initials: buildInitials(normalizedName),
      baseOperacion: (profile?.baseOperacion || '').trim(),
      followersText: formatFollowers(profile?.followers),
      visitsLabel: formatVisitCount(profile?.views),
      views: Number.isFinite(Number(profile?.views)) ? Number(profile.views) : 0,
      phoneNumber: (profile?.phoneNumber || '').trim(),
      email: (profile?.email || '').trim(),
      summaryParagraphs: aboutParagraphs,
      companyName,
      companySlug,
      memberSince: formatDateLabel(profile?.updated),
    };
  }, [profile]);

	useEffect(() => {
		if (!profileIdValue) {
			return;
		}
		if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
			return;
		}

		const visitKey = USER_VISIT_STORAGE_KEY;
		let cache = {};
		try {
			const stored = window.localStorage.getItem(visitKey);
			cache = stored ? JSON.parse(stored) || {} : {};
		} catch {
			cache = {};
		}

		const identifier = String(profileIdValue);
		const now = Date.now();
		const lastVisit = Number(cache[identifier] || 0);
		if (Number.isFinite(lastVisit) && now - lastVisit < USER_VISIT_COOLDOWN_MS) {
			return;
		}

		cache[identifier] = now;
		try {
			window.localStorage.setItem(visitKey, JSON.stringify(cache));
		} catch {
			// ignore storage issues
		}

		const profileUsername = (profileUsernameValue || username || '').trim();
		const viewerId = authModel?.id || '';
		const viewerLabel =
			authModel?.name ||
			authModel?.username ||
			authModel?.email ||
			(viewerId ? 'Miembro de Flotte' : '');
		const viewerUsername = authModel?.username || '';

		registerUserProfileVisit({
			identifier: profileIdValue || profileUsername,
			viewerId,
			viewerLabel,
			viewerUsername,
			profile: profileVisitSummary,
		})
			.then(nextCount => {
				if (typeof nextCount === 'number' && Number.isFinite(nextCount)) {
					setProfile(prev =>
						prev ? { ...prev, views: Math.max(0, Math.round(nextCount)) } : prev
					);
				}
			})
			.catch(error => {
				console.warn('No se pudo registrar la visita del perfil de usuario.', error);
			});
	}, [profileIdValue, profileUsernameValue, username, profileVisitSummary, authModel]);

  useEffect(() => {
    const authorId = profile?.id;
    if (!authorId) {
      setRecentPosts([]);
      setRecentPostsLoading(false);
      return;
    }
    let ignore = false;
    setRecentPostsLoading(true);
    setRecentPostsError('');
    listFeedPosts({ limit: RECENT_ACTIVITY_LIMIT, createdBy: authorId })
      .then(items => {
        if (!ignore) {
          setRecentPosts(items || []);
        }
      })
      .catch(err => {
        if (!ignore) {
          setRecentPostsError(err?.message || 'No pudimos cargar tu actividad más reciente.');
        }
      })
      .finally(() => {
        if (!ignore) {
          setRecentPostsLoading(false);
        }
      });
    return () => {
      ignore = true;
    };
  }, [profile?.id]);

  const hasAvatarImage = Boolean(view.avatarUrl);
  const hasHeroBanner = Boolean(view.heroUrl);
  const heroCoverClass = hasHeroBanner
    ? 'profile-cover profile-header__cover'
    : 'profile-cover profile-cover--empty profile-header__cover';
  const heroJoinedLabel = view.memberSince
    ? `Unido en ${view.memberSince}`
    : view.tagline || '';
  const hasHeaderMeta = Boolean(view.baseOperacion || heroJoinedLabel);
  const isVerifiedProfile = Boolean(
    view.isVerified ?? view.verified ?? view.verificado ?? view.isCompanyVerified
  );

  const handleHeroClick = () => {
    if (!isSelf || updatingHero) return;
    heroInputRef.current?.click();
  };

  const handleHeroKeyDown = event => {
    if (!isSelf) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleHeroClick();
    }
  };

  const handleHeroFileChange = async event => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUpdatingHero(true);
    try {
      await updateUserHero(file);
      await refreshProfile();
    } catch (err) {
      setError(err?.message || 'No se pudo actualizar el banner.');
    } finally {
      setUpdatingHero(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleAvatarClick = () => {
    if (!isSelf || updatingAvatar) return;
    avatarInputRef.current?.click();
  };

  const handleAvatarKeyDown = event => {
    if (!isSelf) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleAvatarClick();
    }
  };

  const handleAvatarFileChange = async event => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUpdatingAvatar(true);
    try {
      await updateUserAvatar(file);
      await refreshProfile();
    } catch (err) {
      setError(err?.message || 'No se pudo actualizar la foto de perfil.');
    } finally {
      setUpdatingAvatar(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const startAboutEditing = () => {
    if (!isSelf) return;
    setAboutDraft(profile?.aboutMe || '');
    setIsEditingAbout(true);
  };

  const cancelAboutEditing = () => {
    setIsEditingAbout(false);
    setAboutDraft(profile?.aboutMe || '');
  };

  const handleAboutSave = async () => {
    setSavingAbout(true);
    try {
      await updateUserAboutMe(aboutDraft);
      await refreshProfile();
      setIsEditingAbout(false);
    } catch (err) {
      setError(err?.message || 'No se pudo guardar la descripción.');
    } finally {
      setSavingAbout(false);
    }
  };

  const startContactEditing = () => {
    if (!isSelf) return;
    setContactDraft({
      phoneNumber: profile?.phoneNumber || '',
      baseOperacion: profile?.baseOperacion || '',
    });
    setIsEditingContact(true);
  };

  const cancelContactEditing = () => {
    setIsEditingContact(false);
    setContactDraft({
      phoneNumber: profile?.phoneNumber || '',
      baseOperacion: profile?.baseOperacion || '',
    });
  };

  const handleContactInputChange = event => {
    const { name, value } = event.target;
    setContactDraft(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationSuggestionSelect = suggestion => {
    setContactDraft(prev => ({
      ...prev,
      baseOperacion: suggestion.display || suggestion.name || suggestion.locode || '',
    }));
    setLocationSuggestions([]);
  };

  const handleContactSave = async event => {
    event.preventDefault();
    setSavingContact(true);
    try {
      await updateUserProfileBasics({
        phoneNumber: contactDraft.phoneNumber,
        baseOperacion: contactDraft.baseOperacion,
      });
      setProfile(prev =>
        prev
          ? {
              ...prev,
              phoneNumber: contactDraft.phoneNumber,
              baseOperacion: contactDraft.baseOperacion,
            }
          : prev
      );
      await refreshProfile();
      setIsEditingContact(false);
      setLocationSuggestions([]);
    } catch (err) {
      setError(err?.message || 'No se pudieron actualizar los datos de contacto.');
    } finally {
      setSavingContact(false);
    }
  };

  const startPasswordEditing = () => {
    if (!isSelf) return;
    setPasswordDraft({ newPassword: '', confirmPassword: '' });
    setIsEditingPassword(true);
  };

  const cancelPasswordEditing = () => {
    setIsEditingPassword(false);
    setPasswordDraft({ newPassword: '', confirmPassword: '' });
  };

  const handlePasswordInputChange = event => {
    const { name, value } = event.target;
    setPasswordDraft(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordSave = async event => {
    event.preventDefault();
    const nextPassword = passwordDraft.newPassword.trim();
    if (!nextPassword) {
      setError('La contraseña no puede estar vacía.');
      return;
    }
    if (nextPassword.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (passwordDraft.newPassword !== passwordDraft.confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setSavingPassword(true);
    try {
      await updateUserPassword(nextPassword);
      setIsEditingPassword(false);
      setPasswordDraft({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError(err?.message || 'No se pudo actualizar la contraseña.');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-shell">
        <div className="profile-layout">
          <main className="profile-main">
            <section className="profile-header skeleton-card">
              <div className="profile-header__cover skeleton-block" />
              <div className="profile-header__strip">
                <div className="profile-header__avatar skeleton-block" />
                <div className="skeleton-stack" style={{ width: '60%' }}>
                  <span className="skeleton-line" style={{ width: '70%', height: 26 }} />
                  <span className="skeleton-line" style={{ width: '55%' }} />
                </div>
                <span className="skeleton-line" style={{ width: 140, height: 40 }} />
              </div>
            </section>
            <div className="profile-content-grid">
              <div className="profile-column profile-column--sidebar">
                <section className="profile-card skeleton-card">
                  <span className="skeleton-line" style={{ width: '50%', height: 18 }} />
                  <span className="skeleton-line" />
                  <span className="skeleton-line" style={{ width: '70%' }} />
                </section>
              </div>
              <div className="profile-column profile-column--main">
                <section className="profile-card profile-summary skeleton-card">
                  <span className="skeleton-line" style={{ width: '40%', height: 20 }} />
                  <span className="skeleton-line" />
                  <span className="skeleton-line" style={{ width: '80%' }} />
                </section>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-shell">
      {error ? <div className="profile-status error">{error}</div> : null}

      <div className="profile-layout">
        <main className="profile-main">
          <section className={`profile-header${isSelf ? ' profile-header--editable' : ''}`}>
            <div
              className={heroCoverClass}
              role={isSelf ? 'button' : undefined}
              tabIndex={isSelf ? 0 : undefined}
              onClick={isSelf ? handleHeroClick : undefined}
              onKeyDown={isSelf ? handleHeroKeyDown : undefined}
              aria-label={isSelf ? 'Editar banner' : undefined}
            >
              {hasHeroBanner ? (
                <img src={view.heroUrl} alt={`Banner de ${view.name}`} loading="lazy" />
              ) : null}
              {isSelf ? (
                <span className={`profile-cover-edit${updatingHero ? ' is-loading' : ''}`}>
                  <FaCamera />
                </span>
              ) : null}
            </div>
            <div className="profile-header__strip">
              <div
                className={`profile-avatar profile-header__avatar${isSelf ? ' profile-avatar--editable' : ''}`}
                role={isSelf ? 'button' : undefined}
                tabIndex={isSelf ? 0 : undefined}
                onClick={isSelf ? handleAvatarClick : undefined}
                onKeyDown={isSelf ? handleAvatarKeyDown : undefined}
                aria-label={isSelf ? 'Actualizar foto de perfil' : undefined}
              >
                <img
                  src={view.avatarUrl || DEFAULT_USER_AVATAR}
                  alt={`Foto de ${view.name}`}
                  data-placeholder={hasAvatarImage ? undefined : 'true'}
                />
                {isSelf ? (
                  <span className={`profile-avatar-edit${updatingAvatar ? ' is-loading' : ''}`}>
                    <FaCamera />
                  </span>
                ) : null}
              </div>
              <div className="profile-header__info">
                <div className="profile-header__headline">
                  <h1>{view.name}</h1>
                  {isVerifiedProfile ? (
                    <span className="profile-header__badge" title="Perfil verificado" aria-label="Perfil verificado">
                      <FaCheckCircle aria-hidden="true" />
                    </span>
                  ) : null}
                  {view.username ? <p className="profile-username">@{view.username}</p> : null}
                </div>
                {hasHeaderMeta ? (
                  <div className="profile-header__details">
                    {view.baseOperacion ? (
                      <span className="profile-header__details-item">
                        <FaMapMarkerAlt aria-hidden="true" />
                        {view.baseOperacion}
                      </span>
                    ) : null}
                    {view.baseOperacion && heroJoinedLabel ? (
                      <span className="profile-header__dot" aria-hidden="true">
                        &bull;
                      </span>
                    ) : null}
                    {heroJoinedLabel ? (
                      <span className="profile-header__details-item">
                        <FaCalendarAlt aria-hidden="true" />
                        {heroJoinedLabel}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {view.followersText || view.visitsLabel ? (
                  <div className="profile-header__meta">
                    {view.followersText ? <span>{view.followersText}</span> : null}
                    {view.visitsLabel ? <span>{view.visitsLabel}</span> : null}
                  </div>
                ) : null}
              </div>
              {view.email ? (
                <a href={`mailto:${view.email}`} className="profile-header__cta">
                  Contactar
                </a>
              ) : null}
            </div>
          </section>
          {isSelf ? (
            <input
              ref={heroInputRef}
              type="file"
              accept="image/*"
              className="profile-file-input"
              onChange={handleHeroFileChange}
              hidden
            />
          ) : null}
          {isSelf ? (
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="profile-file-input"
              onChange={handleAvatarFileChange}
              hidden
            />
          ) : null}

          <div className="profile-content-grid">
            <div className="profile-column profile-column--sidebar">
              <section className="profile-card profile-user-contact">
                <div className="profile-card-head">
                  <h3>Datos de contacto</h3>
                  {isSelf ? (
                    <button
                      type="button"
                      className="profile-inline-action"
                      onClick={isEditingContact ? cancelContactEditing : startContactEditing}
                      aria-label={isEditingContact ? 'Cancelar edición de contacto' : 'Editar datos de contacto'}
                    >
                      <FaPen />
                    </button>
                  ) : null}
                </div>
                {isSelf && isEditingContact ? (
                  <form className="profile-contact-form" onSubmit={handleContactSave}>
                    <label htmlFor="contact-phone">Teléfono</label>
                    <input
                      id="contact-phone"
                      type="tel"
                      name="phoneNumber"
                      value={contactDraft.phoneNumber}
                      onChange={handleContactInputChange}
                      placeholder="Ej. 55 1234 5678"
                    />
                    <label htmlFor="contact-location">Ubicación</label>
                    <input
                      id="contact-location"
                      type="text"
                      name="baseOperacion"
                      value={contactDraft.baseOperacion}
                      onChange={handleContactInputChange}
                      placeholder="Ciudad, Pais"
                    />
                    {locationLoading ? (
                      <p className="profile-hint">Buscando ubicaciones...</p>
                    ) : locationSuggestions.length ? (
                      <ul className="profile-location-suggestions">
                        {locationSuggestions.map(item => (
                          <li key={item.locode}>
                            <button
                              type="button"
                              onClick={() => handleLocationSuggestionSelect(item)}
                            >
                              <span>{item.display || item.name}</span>
                              <strong>{item.locode}</strong>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    <div className="profile-inline-actions">
                      <button type="submit" className="profile-btn primary" disabled={savingContact}>
                        {savingContact ? 'Guardando...' : 'Guardar'}
                      </button>
                      <button
                        type="button"
                        className="profile-btn secondary"
                        onClick={cancelContactEditing}
                        disabled={savingContact}
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                ) : (
                  <ul className="profile-info-list profile-info-list--icons">
                    {[
                      {
                        key: 'email',
                        label: 'Correo',
                        value: view.email || 'No disponible',
                        href: view.email ? `mailto:${view.email}` : '',
                        icon: <FaEnvelope />,
                      },
                      {
                        key: 'phone',
                        label: 'Teléfono',
                        value: view.phoneNumber || 'No disponible',
                        href: view.phoneNumber ? `tel:${view.phoneNumber}` : '',
                        icon: <FaPhone />,
                      },
                      {
                        key: 'location',
                        label: 'Ubicación',
                        value: view.baseOperacion || 'No disponible',
                        icon: <FaMapMarkerAlt />,
                      },
                    ].map(item => (
                      <li key={item.key} className="profile-info-item">
                        <span className="profile-info-icon" aria-hidden="true">
                          {item.icon}
                        </span>
                        <div>
                          <span className="profile-info-label">{item.label}</span>
                          <span className="profile-info-value">
                            {item.href ? (
                              <a href={item.href} className="profile-info-link">
                                {item.value}
                              </a>
                            ) : (
                              item.value
                            )}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <ReviewsShowcase
                containerClassName="profile-card profile-review-card"
                title="Evaluación como cliente"
                subtitle="Opiniones de empresas con las que colaboró"
                icon={null}
                reviews={clientReviews}
                loading={clientReviewsLoading}
                emptyMessage="Aún no hay evaluaciones públicas para este usuario."
                maxVisible={3}
              />

              {view.companyName ? (
                <section className="profile-card profile-user-company">
                  <h3>Empresa vinculada</h3>
                  <div className="profile-company-card">
                    <div className="profile-company-logo">{buildInitials(view.companyName)}</div>
                    <div className="profile-company-meta">
                      <p className="profile-company-name">{view.companyName}</p>
                      <span className="profile-company-label">Miembro vinculado</span>
                    </div>
                    {view.companySlug ? (
                      <Link
                        to={`/empresa/${view.companySlug}`}
                        className="profile-btn secondary profile-company-action"
                      >
                        Ver perfil
                      </Link>
                    ) : null}
                  </div>
                </section>
              ) : null}

              {isSelf ? (
                <section className="profile-card profile-user-security">
                  <div className="profile-card-head">
                    <h3>Seguridad</h3>
                    <button
                      type="button"
                      className="profile-inline-action"
                      onClick={isEditingPassword ? cancelPasswordEditing : startPasswordEditing}
                      aria-label={isEditingPassword ? 'Cancelar cambio de contraseña' : 'Actualizar contraseña'}
                    >
                      <FaPen />
                    </button>
                  </div>
                  {isEditingPassword ? (
                    <form className="profile-contact-form" onSubmit={handlePasswordSave}>
                      <label htmlFor="password-new">Nueva contraseña</label>
                      <input
                        id="password-new"
                        type="password"
                        name="newPassword"
                        value={passwordDraft.newPassword}
                        onChange={handlePasswordInputChange}
                        placeholder="••••••••"
                      />
                      <label htmlFor="password-confirm">Confirmar contraseña</label>
                      <input
                        id="password-confirm"
                        type="password"
                        name="confirmPassword"
                        value={passwordDraft.confirmPassword}
                        onChange={handlePasswordInputChange}
                        placeholder="••••••••"
                      />
                      <p className="profile-hint">Asegúrate de usar al menos 8 caracteres.</p>
                      <div className="profile-inline-actions">
                        <button type="submit" className="profile-btn primary" disabled={savingPassword}>
                          {savingPassword ? 'Actualizando...' : 'Actualizar contraseña'}
                        </button>
                        <button
                          type="button"
                          className="profile-btn ghost"
                          onClick={cancelPasswordEditing}
                          disabled={savingPassword}
                        >
                          Cancelar
                        </button>
                      </div>
                    </form>
                  ) : (
                    <p className="profile-hint">
                      Mantén tu cuenta segura cambiando tu contraseña periódicamente.
                    </p>
                  )}
                </section>
              ) : null}
            </div>
            <div className="profile-column profile-column--main">
              <section className="profile-card profile-summary">
                <div className="profile-summary-header">
                  <h2>Acerca de mi</h2>
                  {isSelf ? (
                    <button
                      type="button"
                      className="profile-inline-action"
                      onClick={isEditingAbout ? cancelAboutEditing : startAboutEditing}
                      aria-label={isEditingAbout ? 'Cancelar edición' : 'Editar descripción'}
                    >
                      <FaPen />
                    </button>
                  ) : null}
                </div>
                {isSelf && isEditingAbout ? (
                  <>
                    <textarea
                      className="profile-textarea"
                      rows={5}
                      value={aboutDraft}
                      onChange={event => setAboutDraft(event.target.value)}
                      placeholder="Comparte un poco sobre tu experiencia, proyectos o lo que te inspira."
                    />
                    <div className="profile-inline-actions">
                      <button
                        type="button"
                        className="profile-btn primary"
                        onClick={handleAboutSave}
                        disabled={savingAbout}
                      >
                        {savingAbout ? 'Guardando...' : 'Guardar'}
                      </button>
                      <button
                        type="button"
                        className="profile-btn secondary"
                        onClick={cancelAboutEditing}
                        disabled={savingAbout}
                      >
                        Cancelar
                      </button>
                    </div>
                  </>
                ) : view.summaryParagraphs.length ? (
                  view.summaryParagraphs.map((text, index) => <p key={index}>{text}</p>)
                ) : (
                  <p className="profile-summary-empty">
                    Este usuario aun no ha agregado informacion en su biografia.
                  </p>
                )}
              </section>

              <section className="profile-card profile-activity-card">
                <div className="profile-card-head">
                  <div>
                    <h3>Actividad reciente</h3>
                    <p className="muted">
                      Comparte publicaciones o postea para mostrarlas aqui.
                    </p>
                  </div>
                </div>
                {recentPostsLoading ? (
                  <p className="profile-summary-empty profile-activity-loader">
                    Cargando actividad más reciente...
                  </p>
                ) : recentPostsError ? (
                  <p className="profile-summary-empty profile-activity-error">{recentPostsError}</p>
                ) : recentPosts.length ? (
                  <div className="profile-activity-timeline">
                    {recentPosts.map((post, index) => {
                      const descriptor = buildActivityDescriptor(post);
                      const Component = descriptor.href ? 'a' : 'div';
                      const itemClasses = ['activity-timeline__item'];
                      if (descriptor.type === 'request') itemClasses.push('activity-timeline__item--request');
                      const itemProps = descriptor.href
                        ? {
                            href: descriptor.href,
                            target: descriptor.external ? '_blank' : undefined,
                            rel: descriptor.external ? 'noopener noreferrer' : undefined,
                          }
                        : {};
                      return (
                        <Component
                          key={post.id}
                          className={itemClasses.join(' ')}
                          data-last={index === recentPosts.length - 1 ? 'true' : 'false'}
                          {...itemProps}
                        >
                          <span className="activity-timeline__marker-dot" aria-hidden="true" />
                          <span className="activity-timeline__icon" aria-hidden="true">
                            {descriptor.icon === 'request' ? <FaFileAlt /> : <FaBullhorn />}
                          </span>
                          <div className="activity-timeline__body">
                            <div className="activity-timeline__header">
                              <span className={`activity-timeline__label activity-timeline__label--${descriptor.type}`}>
                                {descriptor.label}
                              </span>
                              <span className="activity-timeline__date">{descriptor.dateLabel}</span>
                            </div>
                            <p className="activity-timeline__title" title={descriptor.title}>
                              {descriptor.title}
                            </p>
                            {descriptor.metaPill ? (
                              <div className="activity-timeline__meta">
                                <span className="activity-timeline__pill">{descriptor.metaPill}</span>
                              </div>
                            ) : null}
                          </div>
                        </Component>
                      );
                    })}
                  </div>
                ) : (
                  <p className="profile-summary-empty">
                    Estamos preparando este espacio para tus proximas novedades.
                  </p>
                )}
              </section>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
